import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Lightning, Eye, EyeSlash, CheckCircle, Warning } from "@phosphor-icons/react";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { API } from "../App";

/**
 * /reset-password?token=...
 * Lets a user set a new password using a single-use token emailed via Resend.
 */
const ResetPassword = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("This reset link is missing its token. Request a new one from the sign-in screen.");
    }
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("The two passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setSuccess(true);
      // Auto-redirect after 3s so the user can read the success message.
      setTimeout(() => navigate("/", { replace: true }), 3000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-black p-4" data-testid="reset-password-page">
      <Card className="w-full max-w-md bg-zinc-950 border-zinc-800">
        <CardContent className="p-8">
          <div className="flex items-center gap-2 mb-6">
            <Lightning weight="fill" className="w-6 h-6 text-emerald-500" />
            <span className="text-lg font-bold text-white">FlashGrants</span>
          </div>

          {success ? (
            <div className="text-center" data-testid="reset-password-success">
              <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
                <CheckCircle weight="fill" className="w-7 h-7 text-emerald-400" />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-white">Password updated</h2>
              <p className="text-zinc-400 mb-6 text-sm">
                You can now sign in with your new password. Redirecting you to the home page…
              </p>
              <Button
                onClick={() => navigate("/", { replace: true })}
                className="w-full bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm h-11"
                data-testid="reset-password-back-btn"
              >
                Back to FlashGrants
              </Button>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold mb-1 text-white">Set a new password</h2>
              <p className="text-zinc-400 text-sm mb-6">
                Choose a password you'll remember. You'll need to sign in again on every device after resetting.
              </p>

              {error && (
                <div
                  className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3 rounded-sm mb-4 flex items-start gap-2"
                  data-testid="reset-password-error"
                >
                  <Warning weight="fill" className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">New password</label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      minLength={6}
                      required
                      autoFocus
                      className="bg-zinc-900 border-zinc-700 text-white h-11 pr-10"
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Confirm new password</label>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Re-enter the same password"
                    minLength={6}
                    required
                    className="bg-zinc-900 border-zinc-700 text-white h-11"
                    data-testid="reset-password-confirm-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading || !token}
                  className="w-full bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm h-11"
                  data-testid="reset-password-submit-btn"
                >
                  {loading ? <div className="loading-spinner mr-2" /> : null}
                  Reset Password
                </Button>
              </form>

              <p className="text-xs text-zinc-600 text-center mt-6">
                Reset links expire 30 minutes after they're sent. If yours has expired, just request a new one.
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
